import React from 'react'
import PropTypes from 'prop-types'
import {
    Container,
    Typography,
    Box,
    Paper,
    Link,
    Divider,
    Grid,
    Stack,
    Chip,
} from '@mui/material'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'

import globalConfig from '../../config/globalConfig'

// ─── datos ────────────────────────────────────────────────────────────────────

const SPECS_COMUNES = [
    [
        'Frecuencia de actualización',
        '4 veces al día (00:00, 06:00, 12:00 y 18:00 UTC)',
    ],
    ['Período de pronóstico', '72 horas'],
    ['Resolución espacial', '0.25° (aproximadamente 27 × 27 km)'],
    ['Cobertura utilizada', '29°S a 38°S y 60°W a 45°W (área del mapa)'],
]

const MODELOS = [
    {
        nombre: 'GFS',
        subtitulo: 'Global Forecast System',
        descripcion: null,
        specs: SPECS_COMUNES,
        variables: [
            'Ráfagas: intensidad y dirección (nudos y grados)',
            'Precipitación (mm)',
            'Presión atmosférica a nivel del mar (hPa)',
            'Cobertura total de nubes (%)',
            'Temperatura superficie del mar (°C)',
        ],
    },
    {
        nombre: 'GFS Wave',
        subtitulo: 'WAVEWATCH III — NCEP/NOAA',
        descripcion:
            'Es la implementación del modelo GFS con un modelo de olas de ' +
            'tercera generación WAVEWATCH III de NCEP/NOAA. El dominio ' +
            'permite representar la generación y propagación del oleaje en ' +
            'los océanos. En este producto se busca la representación del ' +
            'océano Atlántico Sur (adyacente al territorio uruguayo) y Río ' +
            'de la Plata.',
        specs: SPECS_COMUNES,
        variables: [
            'Viento: intensidad y dirección (nudos y grados)',
            'Altura de olas (m)',
            'Período de olas (s)',
            'Dirección de olas (grados)',
        ],
    },
]

const PUNTOS_METEOGRAMA = [
    { punto: 'Puerto de Colonia', lon: '-57.842943', lat: '-34.476074' },
    { punto: 'Puerto de Juan Lacaze', lon: '-57.445008', lat: '-34.440722' },
    { punto: 'Faro Boca de Cufré', lon: '-57.153108', lat: '-34.450082' },
    { punto: 'Playa Kiyú', lon: '-56.727563', lat: '-34.701704' },
    {
        punto: 'Desembocadura del Río Santa Lucía',
        lon: '-56.4768',
        lat: '-34.8755',
    },
    { punto: 'Puerto de Montevideo', lon: '-56.22263', lat: '-34.913407' },
    { punto: 'Isla de Flores', lon: '-55.932172', lat: '-34.944182' },
    { punto: 'Pontón Recalada', lon: '-55.85', lat: '-35.033333' },
    { punto: 'Puerto de Piriápolis', lon: '-55.281428', lat: '-34.877011' },
    { punto: 'Puerto de Punta del Este', lon: '-54.950936', lat: '-34.963938' },
    { punto: 'Boya José Ignacio', lon: '-54.701944', lat: '-34.884722' },
    { punto: 'Puerto La Paloma', lon: '-54.143056', lat: '-34.653611' },
    { punto: 'Faro Cabo Polonio', lon: '-53.777231', lat: '-34.405727' },
    { punto: 'Faro Punta Palmar', lon: '-53.550835', lat: '-34.066773' },
    { punto: 'Barra del Chuy', lon: '-53.370409', lat: '-33.744357' },
]

// ─── ficha de modelo ──────────────────────────────────────────────────────────

const ModeloCard = ({ modelo }) => (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" component="h2" fontWeight={700}>
            {modelo.nombre}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {modelo.subtitulo}
        </Typography>

        {modelo.descripcion && (
            <Typography
                variant="body2"
                sx={{ mb: 2, lineHeight: 1.7, textAlign: 'justify' }}
            >
                {modelo.descripcion}
            </Typography>
        )}

        <Box sx={{ mb: 2.5 }}>
            {modelo.specs.map(([label, valor]) => (
                <Stack
                    key={label}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 0, sm: 2 }}
                    sx={{
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 210, fontWeight: 600 }}
                    >
                        {label}
                    </Typography>
                    <Typography variant="body2">{valor}</Typography>
                </Stack>
            ))}
        </Box>

        <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 1 }}
        >
            Variables pronosticadas
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {modelo.variables.map((v) => (
                <Chip key={v} label={v} size="small" variant="outlined" />
            ))}
        </Stack>
    </Paper>
)

ModeloCard.propTypes = {
    modelo: PropTypes.object.isRequired,
}

// ─── componente ───────────────────────────────────────────────────────────────

const ReferenciasMarino = () => {
    return (
        <Container maxWidth="md" disableGutters sx={{ py: 1 }}>

            <Typography variant="h5" component="h1" fontWeight={700}>
                Referencias del Pronóstico Marino
            </Typography>
            <Typography
                variant="body1"
                sx={{ mt: 2, mb: 3, lineHeight: 1.7, textAlign: 'justify' }}
            >
                El nuevo formato del Pronóstico ofrecido por el Instituto
                Uruguayo de Meteorología (INUMET) busca presentar información
                meteorológica a los navegantes en una interfaz sencilla y
                amigable, combinando tecnologías y herramientas de modelación
                numérica de predicción con la expertise del equipo de
                meteorólogos del instituto.
            </Typography>

            <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
            >
                Modelos utilizados
            </Typography>
            <Box sx={{ mt: 1 }}>
                {MODELOS.map((modelo) => (
                    <ModeloCard key={modelo.nombre} modelo={modelo} />
                ))}
            </Box>

            <Paper
                variant="outlined"
                sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'grey.50' }}
            >
                <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                    Los productos provenientes de los modelos se encuentran
                    disponibles estimativamente a las H + 5 horas. Por ejemplo,
                    la inicialización de las 06:00 UTC estará disponible en la
                    web a las 11:00 UTC (08:00 hora local).
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    La Situación Sinóptica en superficie y los Avisos se
                    actualizan 4 veces al día, a las 00, 06, 12 y 18 hora local
                    (03, 09, 15 y 21 UTC).
                </Typography>
            </Paper>

            <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
            >
                Puntos de meteograma
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 3 }}>
                {PUNTOS_METEOGRAMA.map((p) => (
                    <Grid key={p.punto} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.5, height: '100%', borderRadius: 2 }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="flex-start"
                            >
                                <PlaceOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: 'primary.main', mt: '2px' }}
                                />
                                <Box>
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        lineHeight={1.3}
                                    >
                                        {p.punto}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="div"
                                        sx={{ mt: 0.5 }}
                                    >
                                        Lat {p.lat} · Lon {p.lon}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Stack spacing={1}>
                <Typography variant="body2">
                    Más información sobre el modelo:{' '}
                    <Link
                        href="https://www.ncei.noaa.gov/products/weather-climate-models/global-forecast"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        NOAA — Global Forecast System
                    </Link>
                </Typography>
                <Typography variant="body2">
                    Por consultas, comunicarse vía email a{' '}
                    <Link href="mailto:contacto@inumet.gub.uy">
                        contacto@inumet.gub.uy
                    </Link>
                </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
                Versión {globalConfig.VERSION}
            </Typography>

        </Container>
    )
}

export default ReferenciasMarino
